import { App } from "@capacitor/app";
import { isRealNativePlatform } from "./platform";

/**
 * "The app is going away" signal, normalized across platforms: Capacitor's
 * appStateChange on native, visibilitychange in the browser.
 *
 * Callers use it to flush anything that must survive a background/kill —
 * the voice draft, for instance. Returns an unsubscribe function.
 */
export function onAppBackgrounded(handler: () => void): () => void {
  if (isRealNativePlatform()) return onNativeAppState(false, handler);

  const onVisibility = () => {
    if (document.visibilityState === "hidden") handler();
  };
  document.addEventListener("visibilitychange", onVisibility);
  return () => document.removeEventListener("visibilitychange", onVisibility);
}

/**
 * The native app came back to the foreground. Browsers report the same
 * moment through visibilitychange, so this registers nothing there.
 */
export function onAppResumed(handler: () => void): () => void {
  return isRealNativePlatform() ? onNativeAppState(true, handler) : () => {};
}

/** Capacitor's appStateChange, on one edge of it. Returns unsubscribe. */
function onNativeAppState(active: boolean, handler: () => void): () => void {
  let remove: (() => void) | null = null;
  let cancelled = false;
  void App.addListener("appStateChange", ({ isActive }) => {
    if (isActive === active) handler();
  }).then((listener) => {
    if (cancelled) {
      void listener.remove();
      return;
    }
    remove = () => void listener.remove();
  });
  return () => {
    cancelled = true;
    remove?.();
  };
}
